/**
 * SEO Landing Page topics.
 *
 * Each entry drives BOTH the runtime React page (src/pages/landing/LandingPage.jsx)
 * AND the postbuild static-HTML emission (scripts/postbuild_seo.cjs) so the raw
 * HTML crawlers see matches the DOM after hydration. Keep both in sync by
 * editing THIS file only — the postbuild script imports the same JS shape.
 *
 * Fields:
 *   slug              → URL path segment (e.g. "fried-chicken-forfar")
 *   title             → Route-specific <title>
 *   description       → Route-specific meta description (~150-170 chars)
 *   h1                → On-page H1
 *   intro             → Lead paragraph (~50-90 words)
 *   sections[]        → { h2, body: string | string[] }
 *   ctaLabel          → Call-to-action button text
 *   focusKeywords     → Human-readable list shown as pills in the UI
 */

const TOPICS = [
  {
    slug: "fried-chicken-forfar",
    title: "Fried Chicken in Forfar — Cravin Crrispy Takeaway & Delivery",
    description:
      "Order southern-style fried chicken in Forfar (DD8) from Cravin Crrispy. Crispy fillet burgers, hot & crrispy wings, chicken strips, boxes and meals for fast delivery and collection until 11pm daily.",
    h1: "Fried Chicken in Forfar",
    intro:
      "Cravin Crrispy is the Forfar takeaway for freshly cooked southern-style fried chicken. Every fillet, strip and wing is hand-breaded and fried to order at 3-5 Queen Street, then delivered hot across DD8, Kirriemuir, Glamis and Letham, or ready for collection within minutes.",
    sections: [
      {
        h2: "What's on the fried chicken menu",
        body: [
          "Crrispy fillet burgers and hot & crrispy variants in a soft toasted seeded bun.",
          "Hot & Crrispy wings and strips in 3-piece, 6-piece and 12-piece boxes.",
          "Crrispy Chicken Box Meals and Combo Meals with fries and a can of juice.",
          "Chicken wraps: classic, peri, tikka, tandoori and hoagie style with chips & cheese.",
        ],
      },
      {
        h2: "Delivery and collection in Forfar",
        body:
          "Fried chicken is delivered across Forfar (DD8) and surrounding villages, or ready for collection at 3-5 Queen Street. Enter your postcode at checkout for the exact fee and minimum order for your street.",
      },
      {
        h2: "Opening hours",
        body:
          "Cravin Crrispy is open every day from 4pm until 11pm. Preorders are accepted from 11am — place a chicken order in the morning and we'll prepare it as soon as the kitchen opens.",
      },
    ],
    ctaLabel: "Order fried chicken now",
    focusKeywords: [
      "fried chicken Forfar",
      "chicken takeaway DD8",
      "Cravin Crrispy chicken",
      "chicken delivery Forfar",
    ],
  },
  {
    slug: "burgers-forfar",
    title: "Burgers in Forfar — Cravin Crrispy Takeaway & Delivery",
    description:
      "Gourmet burgers in Forfar delivered hot. Quarter Pounder, Half Pounder, Mighty Crrispy, Peri, Tikka, Turkish and Korean BBQ burgers with fries and drinks. Order online for delivery or collection.",
    h1: "Burgers in Forfar",
    intro:
      "From the Quarter Pounder Cheeseburger to the Mighty Crrispy and the Korean BBQ burger, every Cravin Crrispy burger is grilled to order and served in a softly toasted seeded bun with our house burger sauce. Delivered fast across Forfar or ready to collect at 3-5 Queen Street.",
    sections: [
      {
        h2: "Popular burger meals",
        body: [
          "Quarter Pounder & Half Pounder Cheeseburgers with fresh salad and burger sauce.",
          "Crrispy Fillet & Mighty Crrispy Burgers stacked with strips, cheese and hash brown.",
          "Peri Burger, Tikka Burger and Tandoori Burger — grilled marinated chicken with salad and mayo.",
          "Turkish Burger topped with donner meat, Korean BBQ Burger and Veggie Burger for meat-free days.",
          "Every burger is available as a meal deal with fries and a can of juice.",
        ],
      },
      {
        h2: "Delivery and collection",
        body:
          "Burger orders are ready in minutes at 3-5 Queen Street, Forfar, or delivered hot to your door across DD8, Kirriemuir, Glamis and Letham. Pay by card, cash on delivery or cash on collection.",
      },
      {
        h2: "When are burgers available?",
        body:
          "The full burger menu is available every day from 4pm to 11pm, with preorders accepted from 11am.",
      },
    ],
    ctaLabel: "Browse the burger menu",
    focusKeywords: [
      "burgers Forfar",
      "burger delivery DD8",
      "Cravin Crrispy burgers",
      "Korean BBQ burger Forfar",
    ],
  },
  {
    slug: "kebabs-forfar",
    title: "Kebabs in Forfar — Donner, Shish & Wraps | Cravin Crrispy",
    description:
      "Fresh donner and shish kebabs in Forfar. Order donner wraps, mixed donner, mexican donner, kebab meals and kids kebabs from Cravin Crrispy for fast delivery across DD8 or collection.",
    h1: "Kebabs in Forfar",
    intro:
      "Cravin Crrispy serves donner and shish kebabs freshly shaved to order. Choose from classic donner, mixed donner, mexican donner (with jalapeños), Turkish burgers and hoagie-style kebab wraps with chips and cheese — all delivered hot across Forfar or ready for collection.",
    sections: [
      {
        h2: "Kebab wraps and meals",
        body: [
          "Donner Wrap — donner in a warm tortilla with lettuce, cucumber, onions, tomatoes and your choice of sauce.",
          "Donner Hoagie Wrap — donner, fries and cheese in a toasted tortilla.",
          "Mexican Donner Wrap — donner with jalapeños and fried onions.",
          "Mixed Donner Wrap — donner and chicken combined.",
          "Turkish Burger — beef burger topped with donner in a seeded bun with salad and sauce.",
        ],
      },
      {
        h2: "Meat quality",
        body:
          "All meat used at Cravin Crrispy is sourced from halal suppliers. We do not claim formal certification — please call 01307 477786 if you'd like the current supplier details.",
      },
      {
        h2: "Delivery area",
        body:
          "Kebabs are delivered across Forfar (DD8), Kirriemuir, Glamis and Letham. Enter your postcode at checkout for delivery fees and minimum order values.",
      },
    ],
    ctaLabel: "Order a kebab now",
    focusKeywords: [
      "kebab Forfar",
      "donner delivery DD8",
      "Turkish burger Forfar",
      "kebab wrap Forfar",
    ],
  },
  {
    slug: "takeaway-delivery-forfar",
    title: "Takeaway Delivery in Forfar — Cravin Crrispy DD8",
    description:
      "Fast takeaway delivery in Forfar from Cravin Crrispy. Order fried chicken, burgers, kebabs, wraps, loaded fries, desserts and drinks online for delivery to your door across DD8 or collection.",
    h1: "Takeaway Delivery in Forfar",
    intro:
      "Cravin Crrispy delivers a full takeaway menu to your door across Forfar and the DD8 postcode area. Order online at cravincrispy.co.uk, choose delivery or collection at checkout, pay by card or cash — and receive an order confirmation with your estimated preparation time.",
    sections: [
      {
        h2: "Delivery area",
        body:
          "We deliver across Forfar, Kirriemuir, Glamis, Letham and nearby Angus villages. Delivery fees and minimum order values depend on your postcode — enter it at checkout for the exact figures.",
      },
      {
        h2: "How to order",
        body: [
          "Browse the menu and add items to your basket.",
          "Choose delivery to your home or collection at 3-5 Queen Street, Forfar.",
          "Pay securely by card, or select cash on delivery / cash on collection.",
          "Receive a confirmation with your estimated preparation time.",
        ],
      },
      {
        h2: "Opening and preorder hours",
        body:
          "The kitchen serves from 4pm to 11pm every day. Preorders are accepted from 11am — place your order in the morning and it will be prepared as soon as the kitchen opens.",
      },
    ],
    ctaLabel: "Start a takeaway order",
    focusKeywords: [
      "takeaway delivery Forfar",
      "food delivery DD8",
      "Forfar takeaway",
      "late night takeaway Forfar",
    ],
  },
  {
    slug: "desserts-forfar",
    title: "Desserts in Forfar — Cravin Crrispy Milkshakes, Churros & Cake",
    description:
      "Fresh desserts delivered in Forfar. Milkshakes, churros, cookie dough, chocolate cake and cake-in-cans from Cravin Crrispy. Order online for fast delivery across DD8 or collection.",
    h1: "Desserts in Forfar",
    intro:
      "Finish your takeaway with a Cravin Crrispy dessert — freshly fried churros dusted with cinnamon sugar, creamy milkshakes, warm cookie dough or a cake-in-a-can. Order alongside your meal and we'll deliver everything hot (or cold) to your door across Forfar.",
    sections: [
      {
        h2: "What's on the dessert menu",
        body: [
          "Churros 6-pieces dusted with cinnamon sugar.",
          "Milkshakes in vanilla, chocolate and strawberry.",
          "Chocolate Cake slices — rich and moist.",
          "Cake in a Can: Dulce de Caramel, Chocolate Fudge, White Chocolate and Biscoff Melts.",
          "Warm cookie dough with ice cream (seasonal).",
        ],
      },
      {
        h2: "Order alongside your meal",
        body:
          "Add desserts to any Cravin Crrispy takeaway order at checkout — no minimum order for the dessert add-on when you're already ordering food.",
      },
      {
        h2: "Delivery hours",
        body:
          "Desserts are served every day from 4pm to 11pm, delivered hot to your door across Forfar (DD8), Kirriemuir, Glamis and Letham.",
      },
    ],
    ctaLabel: "Order desserts now",
    focusKeywords: [
      "desserts Forfar",
      "milkshakes Forfar",
      "churros Forfar",
      "cake delivery DD8",
    ],
  },
];

module.exports = { TOPICS };
